import type { ProcInput, ProcName, ProcOutput } from '@pz/server';

/** Error returned by the server (code is stable and translated in i18n: errors.<code>). */
export class RpcError extends Error {
  constructor(
    public code: string,
    message?: string,
    public details?: unknown,
  ) {
    super(message ?? code);
  }
}

export type Transport = (name: string, input: unknown) => Promise<unknown>;

let transport: Transport = async (name, input) => {
  const res = await fetch(`/api/rpc/${name}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-pz': '1' },
    body: JSON.stringify(input ?? {}),
    credentials: 'same-origin',
  });
  let body: { ok: boolean; data?: unknown; error?: { code: string; message?: string; details?: unknown } };
  try {
    body = await res.json();
  } catch {
    throw new RpcError(res.status === 401 ? 'UNAUTHENTICATED' : 'NETWORK', `HTTP ${res.status}`);
  }
  if (!body.ok) {
    if (body.error?.code === 'UNAUTHENTICATED' || body.error?.code === 'MFA_REQUIRED') onUnauthenticated();
    if (body.error?.code === 'STEP_UP_REQUIRED') onStepUpRequired(body.error.details);
    throw new RpcError(body.error?.code ?? 'INTERNAL', body.error?.message, body.error?.details);
  }
  return body.data;
};

let onUnauthenticated = () => {};
let stepUpHandler: (details?: unknown) => void = () => {};

/**
 * A call needs the additional confirmation (request 31 of round 4, mandatory since round 5): the gate of the module page
 * or, outside the modules (money, D-04), the confirmation dialog asks for it. `details.area` names the protected area.
 */
export function onStepUpRequired(details?: unknown) {
  stepUpHandler(details);
}

export function setStepUpHandler(fn: (details?: unknown) => void) {
  stepUpHandler = fn;
}

export function setTransport(t: Transport) {
  transport = t;
}

export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

export function rpc<N extends ProcName>(name: N, input: ProcInput<N>): Promise<ProcOutput<N>> {
  return transport(name, input) as Promise<ProcOutput<N>>;
}

/** Plain JSON calls to the auth endpoints (production only). */
export async function authCall<T = Record<string, unknown>>(path: string, body?: unknown, method = 'POST'): Promise<T> {
  const res = await fetch(`/api/auth/${path}`, {
    method,
    headers: body ? { 'content-type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  const json = (await res.json().catch(() => ({ ok: false, error: { code: 'NETWORK' } }))) as {
    ok: boolean;
    data?: T;
    error?: { code: string };
  };
  if (!json.ok) throw new RpcError(json.error?.code ?? 'INTERNAL');
  return json.data as T;
}
